import {
    ApplicationRef,
    ComponentFactoryResolver,
    ComponentRef,
    EmbeddedViewRef,
    Inject,
    Injectable,
    Injector,
    Type,
} from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    type MapboxMap,
    UserStoreService,
    VehicleType,
} from '@polaris-customerweb/sharedcomponents.core';
import { VehicleLocation } from '@polaris-customerweb/sharedcomponents.garage';
import {
    Adventure,
    Dealer,
    MapboxService,
    Place,
    PremiumPlace,
    RideArea,
} from '@polaris-customerweb/sharedcomponents.map';
import {
    GeoBounds,
    GeoCoordinate,
    LogService,
} from '@polaris-customerweb/sharedcomponents.shared';
import {
    Geofence,
    PlannedRide,
    PlannedRideViapoint,
    PlannedRideWaypoint,
    TrackedRide,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { GeometryObject } from 'geojson';
import { get, isEmpty, isNumber, isUndefined, merge } from 'lodash-es';
import type {
    AnimationOptions,
    CameraOptions,
    DataDrivenPropertyValueSpecification,
    GeoJSONFeature,
    GeoJSONSource,
    Layer,
    LayerSpecification,
    LayoutSpecification,
    LngLat,
    LngLatBounds,
    LngLatLike,
    MapOptions,
    PaintSpecification,
    PointLike,
    Popup,
    SourceSpecification,
} from 'mapbox-gl';
import { Observable, throwError } from 'rxjs';
import { MapPointPopupComponent } from 'src/app/components/shared/components/map/map-point-popup/map-point-popup.component';
import { RideConditionPopupComponent } from 'src/app/components/shared/components/map/ride-condition-popup/ride-condition-popup.component';
import { GeoJsonFeatureCollection } from 'src/app/models/geojson-feature-collection';
import { MapPoint } from 'src/app/models/map-point';
import { RideCondition } from 'src/app/models/ride-condition';
import { WINDOW } from '../state/app-config/factories/window.factory';
import { LocalStorageService } from './local-storage.service';

@Injectable({ providedIn: 'root' })
export class MapService {
    readonly className = 'MapService';
    defaultZoom = 3;

    private popupDefaults = { closeButton: false, closeOnClick: false };
    private popup: Popup;
    private permanentPopup: Popup;

    private tileSourceUser = 'polaris-riderx';

    private defaultLatitude = 0;
    private defaultLongitude = 0;
    private defaultTransitionZoom = 2;
    private maxBoundsPanFactor = 2;
    private selectedIdsCache: Record<string, string | number> = {};

    private maxSourceLoadAttempts = 1000; // give up after 1 minute
    private sourceLoadedQueue: Record<
        string,
        ((source: GeoJSONSource) => void)[]
    > = {};
    private sourceLoadedObserver: Record<string, number | undefined> = {};
    private sourceLoadAttempts: Record<string, number> = {};

    constructor(
        private appRef: ApplicationRef,
        private componentFactoryResolver: ComponentFactoryResolver,
        private configService: ConfigService,
        private injector: Injector,
        private localStore: LocalStorageService,
        private logService: LogService,
        mapboxService: MapboxService,
        private userStoreService: UserStoreService,
        @Inject(WINDOW) private window: Window
    ) {
        this.popup = new mapboxService.mapbox.Popup(this.popupDefaults);
        this.permanentPopup = new mapboxService.mapbox.Popup({
            ...this.popupDefaults,
        });
    }

    createGeojsonSource(
        geojson?: GeoJSON.Feature | GeoJSON.FeatureCollection
    ): SourceSpecification {
        return {
            data: geojson || new GeoJsonFeatureCollection(),
            type: 'geojson',
        };
    }

    disableDragPan(mapboxMap: MapboxMap): void {
        mapboxMap.dragPan.disable();
    }

    enableDragPan(mapboxMap: MapboxMap): void {
        mapboxMap.dragPan.enable();
    }

    //#region Layers
    getLayerIds(mapboxMap: MapboxMap): Readonly<string[]> {
        if (!mapboxMap) {
            return [];
        }

        const style = mapboxMap.getStyle();
        if (!style) {
            return [];
        }

        return style.layers.map((layer) => layer.id);
    }

    getLayerStyle(
        mapboxMap: MapboxMap,
        layerId: string
    ): LayerSpecification | undefined {
        const layer = mapboxMap.getLayer(layerId);

        if (!layer) {
            this.logService.warn(
                this.className,
                `Missing layer trying to get layer style: ${layerId}`
            );
            return undefined;
        }

        return layer as LayerSpecification;
    }

    /**
     * @description - Insert a layer in the map after an existing layer.
     * @param mapboxMap - An instance of Mapbox Map to insert the layer in.
     * @param layer - Layer to insert, including style definitions.
     * @param insertAfterLayerId - ID of the layer to insert after. Ideally, we would always be replacing an existing
     * layer in the Mapbox style, but we have to have a default here for when the style does not exist.
     * Obviously not ideal, as "waterway-label" can get moved or removed. All layer style definitions should get moved
     * to Mapbox Studio and we should call MapService.replaceLayer instead of this method.
     */
    insertLayer(
        mapboxMap: MapboxMap,
        layer: LayerSpecification,
        insertAfterLayerId = 'waterway-label'
    ): void {
        if (mapboxMap.getLayer(layer.id)) {
            mapboxMap.removeLayer(layer.id);
        }
        mapboxMap.addLayer(layer, insertAfterLayerId);

        this.logService.debug(this.className, {
            debugMessage: 'layer added',
            layer,
        });
    }

    addLayer(mapboxMap: MapboxMap, layer: LayerSpecification): void {
        if (mapboxMap.getLayer(layer.id)) {
            mapboxMap.removeLayer(layer.id);
        }
        mapboxMap.addLayer(layer);
    }

    replaceLayer(
        mapboxMap: MapboxMap,
        layerId: string,
        sourceId: string,
        overrides: Partial<LayerSpecification> = {},
        visible = true
    ): void {
        const newStyleLayer = this.copyMapLayer(
            mapboxMap,
            layerId,
            sourceId,
            overrides,
            visible
        );

        const layerIds = this.getLayerIds(mapboxMap);

        const layerIndex = layerIds.indexOf(layerId);
        let insertAfterLayerId = '';

        // append layer if existing layer is the top
        if (layerIndex >= layerIds.length - 1) {
            this.addLayer(mapboxMap, newStyleLayer);
        } else {
            if (layerIndex >= 0) {
                // layer IDs are in reverse order, so add one here instead of subtract one.
                insertAfterLayerId = layerIds[layerIndex + 1];
            }

            this.insertLayer(mapboxMap, newStyleLayer, insertAfterLayerId);
        }
    }

    cloneLayer(
        mapboxMap: MapboxMap,
        layerId: string,
        newLayerId: string,
        sourceId: string,
        insertAfterLayerId?: string,
        visible = true
    ): void {
        const newStyleLayer = this.copyMapLayer(
            mapboxMap,
            layerId,
            sourceId,
            undefined,
            visible
        );
        newStyleLayer.id = newLayerId;

        this.insertLayer(
            mapboxMap,
            newStyleLayer,
            insertAfterLayerId || layerId
        );
    }

    removeLayer(mapboxMap: MapboxMap, layerName: string): void {
        if (this.hasLayer(mapboxMap, layerName)) {
            mapboxMap.removeLayer(layerName);
        }
    }

    hideLayer(mapboxMap: MapboxMap, layerId: string): void {
        if (!this.hasLayer(mapboxMap, layerId)) {
            this.logService.warn(
                this.className,
                `Tried to hide non-existent layer: ${layerId}`
            );
            return;
        }

        mapboxMap.setLayoutProperty(layerId, 'visibility', 'none');
    }

    showLayer(mapboxMap: MapboxMap, layerId: string): void {
        if (!this.hasLayer(mapboxMap, layerId)) {
            this.logService.warn(
                this.className,
                `Tried to show non-existent layer: ${layerId}`
            );
            return;
        }

        mapboxMap.setLayoutProperty(layerId, 'visibility', 'visible');
    }

    // only tested on orv trails & labels and trail difficulty
    setLayerOpacity(
        mapboxMap: MapboxMap,
        layerId: string,
        opacity: DataDrivenPropertyValueSpecification<number>
    ): void {
        if (!this.hasLayer(mapboxMap, layerId)) {
            this.logService.warn(
                this.className,
                `Tried to paint non-existent layer: ${layerId}`
            );
            return;
        }
        if (layerId.toLowerCase().includes('label')) {
            mapboxMap.setPaintProperty(layerId, 'text-opacity', opacity);
        } else if (layerId.toLowerCase() === 'difficulty') {
            mapboxMap.setPaintProperty(layerId, 'icon-opacity', opacity);
        } else {
            const currOpacity = mapboxMap.getPaintProperty(
                layerId,
                'line-opacity'
            );
            if (Array.isArray(currOpacity)) {
                currOpacity[currOpacity.length - 1] = opacity;
                mapboxMap.setPaintProperty(
                    layerId,
                    'line-opacity',
                    currOpacity
                );
            } else {
                mapboxMap.setPaintProperty(layerId, 'line-opacity', opacity);
            }
        }
    }
    //#endregion

    addSource(
        mapboxMap: MapboxMap,
        sourceName: string,
        source: SourceSpecification
    ): void {
        if (mapboxMap.getSource(sourceName)) {
            mapboxMap.removeSource(sourceName);
        }

        mapboxMap.addSource(sourceName, source);
        this.logService.debug(this.className, `added ${sourceName} source`);
    }

    changeMapBase(
        mapboxMap: MapboxMap,
        isSatellite: boolean,
        isSnow?: boolean
    ): void {
        const mapBaseUri = this.getMapBaseUri(isSatellite, isSnow);
        this.logService.debug(
            this.className,
            `map base changed to ${mapBaseUri}`
        );
        mapboxMap.setStyle(mapBaseUri);
    }

    getMapBaseUri(isSatellite: boolean, isSnow?: boolean): string {
        let mapStyle: string;
        if (isSnow) {
            mapStyle = isSatellite ? 'satelliteSnow' : 'terrainSnow';
        } else {
            mapStyle = isSatellite ? 'satellite' : 'terrain';
        }

        const tileSource = get(
            this.configService.tileSource,
            mapStyle
        ) as string;
        return `mapbox://styles/${this.tileSourceUser}/${tileSource}?optimize=false`;
    }

    //#region Simple pan/zoom
    /**
     * @description Set center and zoom level of map without animating the transition.
     * @param mapboxMap - The map object that the transition will be applied to.
     * @param geoCoordinate - Desired map center.
     * @param zoom - Desired zoom level. If omitted and the map is currently zoomed in further than the
     * default transition zoom level, the map will be zoomed out to the default transition zoom level.
     */
    jumpToCoordinate(
        mapboxMap: MapboxMap,
        geoCoordinate: GeoCoordinate,
        zoom?: number
    ): void {
        if (zoom === undefined) {
            zoom = this.defaultTransitionZoom;
            zoom = Math.min(zoom, this.getZoom(mapboxMap));
        }

        mapboxMap.stop();
        mapboxMap.jumpTo({
            center: geoCoordinate.toCoordinateArray(),
            zoom,
        });
    }

    /**
     * @description Animate map transition to new geoCoordinate without changing zoom level.
     * @param mapboxMap - The map object that the transition will be applied to.
     * @param geoCoordinate - Location to pan to.
     * @param duration - Duration of transition animation in milliseconds. Defaults to 1000.
     */
    panToCoordinate(
        mapboxMap: MapboxMap,
        geoCoordinate: GeoCoordinate,
        duration = 1000
    ): void {
        if (this.configService.browser.ieVersion) {
            this.jumpToCoordinate(
                mapboxMap,
                geoCoordinate,
                this.getZoom(mapboxMap)
            );
            return;
        }

        mapboxMap.stop();
        mapboxMap.panTo(geoCoordinate, { duration });
    }

    /**
     * @description Animate map transition to center on geoCoordinate. Map zoom level changes linearly if zoom is required.
     * @param mapboxMap - The map object that the transition will be applied to.
     * @param geoCoordinate - Desired map center.
     * @param zoom - Desired zoom level. If omitted and the map is currently zoomed in further than the default
     * @param duration
     * transition zoom level, the map will be zoomed out to the default transition zoom level.
     */
    easeToCoordinate(
        mapboxMap: MapboxMap,
        geoCoordinate: GeoCoordinate,
        zoom?: number,
        duration?: number
    ): void {
        if (this.configService.browser.ieVersion) {
            this.jumpToCoordinate(mapboxMap, geoCoordinate, zoom);
            return;
        }

        // TODO: CLI
        // do nothing if map is already at desired location.
        // if (this.geoService.arePointsEquivalent(geoCoordinate, GeoCoordinate.fromMapboxLngLat(map.getCenter()))) {
        //     return;
        // }

        if (zoom === undefined) {
            zoom = this.defaultTransitionZoom;
            zoom = Math.min(zoom, this.getZoom(mapboxMap));
        }

        const options: AnimationOptions & CameraOptions = {
            center: geoCoordinate.toCoordinateArray(),
            zoom,
        };

        if (duration !== undefined) {
            options.duration = duration;
        }

        mapboxMap.stop();
        mapboxMap.easeTo(options);
    }

    /**
     * @description Animate map transition to center on geoCoordinate, zooming out at the start and zooming in at the end.
     * @param mapboxMap - The map object that the transition will be applied to.
     * @param geoCoordinate - Desired map center.
     * @param zoom - Desired zoom level. If omitted and the map is currently zoomed in further than the default
     * transition zoom level, the map will be zoomed out to the default transition zoom level.
     */
    flyToCoordinate(
        mapboxMap: MapboxMap,
        geoCoordinate: GeoCoordinate,
        zoom?: number
    ): void {
        if (this.configService.browser.ieVersion) {
            this.jumpToCoordinate(mapboxMap, geoCoordinate, zoom);
            return;
        }

        // TODO: CLI
        // do nothing if map is already at desired location.
        // if (this.geoService.arePointsEquivalent(geoCoordinate, GeoCoordinate.fromMapboxLngLat(map.getCenter()))) {
        //     return;
        // }

        if (isUndefined(zoom)) {
            zoom = this.defaultTransitionZoom;
            zoom = Math.min(zoom, this.getZoom(mapboxMap));
        }

        mapboxMap.stop();
        mapboxMap.flyTo({
            center: geoCoordinate.toCoordinateArray(),
            zoom,
        });
    }

    /**
     * @description Pan the map a specified amount.
     * @param mapboxMap - The map object that the transition will be applied to.
     * @param coords - pointlike value to pan the map.
     */
    panBy(mapboxMap: MapboxMap, coords: PointLike) {
        mapboxMap.panBy(coords, { duration: 200 });
    }

    /**
     * @description Fit map to GeoBounds without animating the transition.
     * @param mapboxMap - The map object that the transition will be applied to.
     * @param geoBounds - Bounds to fit the map to.
     * @param maxZoom - Bounds will be adjusted to fit this zoom if the resulting zoom would be greater than it.
     */
    jumpToBounds(
        mapboxMap: MapboxMap,
        geoBounds: GeoBounds,
        maxZoom?: number
    ): void {
        const options: MapOptions['fitBoundsOptions'] = {
            duration: 0,
            linear: true,
        };

        if (maxZoom) {
            options.maxZoom = maxZoom;
        }

        mapboxMap.stop();
        mapboxMap.fitBounds(geoBounds.toMapboxLngLatBounds(), options);
    }

    /**
     * @description Animate map transition to tightly fit a geoBounds. Map zoom level changes linearly if zoom is required.
     * @param mapboxMap - The map object that the transition will be applied to.
     * @param geoBounds - Bounds to fit the map to.
     * @param maxZoom - Bounds will be adjusted to fit this zoom if the resulting zoom would be greater than it.
     * @param duration - The amount of time the transition should take in milliseconds.
     */
    easeToBounds(
        mapboxMap: MapboxMap,
        geoBounds: GeoBounds,
        maxZoom?: number,
        duration?: number
    ): void {
        const options: MapOptions['fitBoundsOptions'] = {
            linear: true,
        };

        if (this.configService.browser.ieVersion) {
            this.jumpToBounds(mapboxMap, geoBounds, maxZoom);
            return;
        }

        if (maxZoom !== undefined) {
            options.maxZoom = maxZoom;
        }

        if (duration !== undefined) {
            options.duration = duration;
        }

        mapboxMap.stop();
        mapboxMap.fitBounds(geoBounds.toMapboxLngLatBounds(), options);
    }

    stopAnimation(mapboxMap: MapboxMap): void {
        mapboxMap.stop();
    }
    //#endregion

    //#region Complex pan/zoom
    /**
     * @description Animate map transition to fit the desired GeoCoordinate inside 80% of the current bounding box.
     * Does nothing if GeoCoordinate is within reduced bounds.
     * Map is panned without zooming if GeoCoordinate is less than half a bounds width outside of the reduced bounds.
     * @param mapboxMap - The map object that the transition will be applied to.
     * @param geoCoordinate - Desired geoCoordinate to include in the map view.
     * @param maxZoom - Bounds will be adjusted to fit this zoom if the resulting zoom would be greater than it.
     */
    panMapToIncludeGeoCoordinate(
        mapboxMap: MapboxMap,
        geoCoordinate: GeoCoordinate,
        maxZoom = 11
    ): void {
        if (this.isCoordinateWithinPanningDistance(mapboxMap, geoCoordinate)) {
            const bounds = this.getBounds(mapboxMap);
            const resizedBounds = bounds.resizedBy(0.8);

            if (resizedBounds.containsGeoCoordinate(geoCoordinate)) {
                return;
            }

            const shiftedReducedBounds =
                resizedBounds.shiftedToIncludeGeoCoordinate(geoCoordinate);
            const shiftedBounds = shiftedReducedBounds.resizedBy(1.25);

            this.easeToBounds(mapboxMap, shiftedBounds, maxZoom);
        } else {
            this.jumpToCoordinate(mapboxMap, geoCoordinate, maxZoom);
        }
    }

    /**
     * @description Pan map to center on GeoCoordinate. Snaps to coordinate if the target coordinate is greater than
     * maxBoundsWidthForPan widths away. Pans in all other cases.
     * @param mapboxMap - The map object to apply the transition to.
     * @param geoCoordinate - The GeoCoordinate the map should center on.
     * @param zoom - Map will be zoomed to this level if set or not at all if not set.
     * @param duration - The amount of time the transition should take in milliseconds.
     */
    panMapToCenterOnGeoCoordinate(
        mapboxMap: MapboxMap,
        geoCoordinate: GeoCoordinate,
        zoom?: number,
        duration?: number
    ): void {
        if (!this.isCoordinateWithinPanningDistance(mapboxMap, geoCoordinate)) {
            this.jumpToCoordinate(mapboxMap, geoCoordinate, zoom);
        } else {
            this.easeToCoordinate(mapboxMap, geoCoordinate, zoom, duration);
        }
    }

    /**
     * @description Pan map to fit bounds. Snaps to bounds if the target bounds is greater than maxBoundsWidthForPan widths away.
     * Pans to bounds in all other cases, zooming to fit target bounds.
     * @param mapboxMap - The map object to apply the transition to.
     * @param targetBounds - The GeoBounds the map should center to.
     * @param maxZoom - Bounds will be adjusted to fit this zoom if the resulting zoom would be greater than it.
     * @param duration - The amount of time the transition should take in milliseconds.
     */
    panMapToBounds(
        mapboxMap: MapboxMap,
        targetBounds: GeoBounds,
        maxZoom?: number,
        duration?: number
    ): void {
        if (!targetBounds?.isValid()) {
            return;
        }

        if (!this.isBoundsWithinPanningDistance(mapboxMap, targetBounds)) {
            this.jumpToBounds(mapboxMap, targetBounds, maxZoom);
        } else {
            this.easeToBounds(mapboxMap, targetBounds, maxZoom, duration);
        }
    }

    /**
     * @description Pan map to include bounds, minimizing map animation. Does nothing if the target bounds is already contained in
     * the current map bounds. Snaps to and tightly fits target bounds if the target bounds is greater than
     * maxBoundsWidthForPan widths away. Pans in all other cases; zooming only if the target bounds is larger than the
     * current bounds.
     * @param mapboxMap - The map object to apply the transition to.
     * @param bounds - The GeoBounds the map should pan to.
     * @param targetBounds
     * @param bufferFactor - Amount to decrease current bounds size when testing if target bounds is inside current
     * bounds. Percentage in decimal form.
     */
    panMapToIncludeBounds(
        mapboxMap: MapboxMap,
        targetBounds: GeoBounds,
        bufferFactor = 0.2
    ): void {
        if (!targetBounds.isValid()) {
            return;
        }

        const mapBounds = this.getBounds(mapboxMap);
        if (
            mapBounds
                .resizedBy(1 - bufferFactor)
                .containsGeoBounds(targetBounds)
        ) {
            return;
        }

        const bufferedBounds = targetBounds.resizedBy(
            1 + 1 / (1 - bufferFactor)
        );

        // if target bounds is greater than maxBoundsWidthForPan widths away, snap to target bounds
        if (!this.isBoundsWithinPanningDistance(mapboxMap, bufferedBounds)) {
            this.jumpToBounds(mapboxMap, bufferedBounds);
        }
        // if new bounds is larger than current bounds, pan map to new bounds.
        else if (bufferedBounds.isLargerThan(mapBounds)) {
            this.easeToBounds(mapboxMap, bufferedBounds);
        }
        // if new bounds is smaller than or equal to current bounds, pan map to include all points of new bounds
        // without decreasing size of current bounds.
        else {
            // calculate buffer for target bounds based on current map size and buffer factor
            const lngBuffer = mapBounds.widthInDegrees * (bufferFactor / 2);
            const latBuffer = mapBounds.heightInDegrees * (bufferFactor / 2);

            const shiftToBounds = new GeoBounds(
                targetBounds.west - lngBuffer,
                targetBounds.south - latBuffer,
                targetBounds.east + lngBuffer,
                targetBounds.north + latBuffer
            );
            const shiftedBounds =
                mapBounds.shiftedToIncludeBounds(shiftToBounds);

            this.easeToBounds(mapboxMap, shiftedBounds);
        }
    }
    //#endregion

    getBounds(mapboxMap: MapboxMap): GeoBounds {
        return GeoBounds.fromMapboxLngLatBounds(
            mapboxMap.getBounds() as LngLatBounds
        );
    }

    getCenter(mapboxMap: MapboxMap): LngLat {
        return mapboxMap.getCenter();
    }

    setMapCenterOverride(latitude: number, longitude: number): void {
        this.localStore.set('mapCenterOverride', { latitude, longitude });
    }

    removeMapCenterOverride(): void {
        this.localStore.remove('mapCenterOverride');
    }

    getFeaturesAtPoint(
        mapboxMap: MapboxMap,
        point: PointLike,
        layerNames?: string[]
    ): GeoJSONFeature[] {
        const options = layerNames ? { layers: layerNames } : undefined;
        return mapboxMap.queryRenderedFeatures(point, options);
    }

    getMapCenterOverride(): GeoCoordinate | undefined {
        const storedMapCenter = this.localStore.get<{
            latitude: number;
            longitude: number;
        }>('mapCenterOverride');
        const latitude = get(storedMapCenter, 'latitude');
        const longitude = get(storedMapCenter, 'longitude');

        if (!isNumber(latitude) || !isNumber(longitude)) {
            return undefined;
        }

        return new GeoCoordinate(longitude, latitude);
    }

    getInitialMapCenter(): GeoCoordinate | undefined {
        const storedMapCenter = this.userStoreService.get<{
            latitude: number;
            longitude: number;
        }>('mapCenter');
        const latitude = get(storedMapCenter, 'latitude');
        const longitude = get(storedMapCenter, 'longitude');

        if (!isNumber(latitude) || !isNumber(longitude)) {
            return undefined;
        }

        return new GeoCoordinate(longitude, latitude);
    }

    getDefaultMapCenter(): GeoCoordinate {
        return new GeoCoordinate(this.defaultLongitude, this.defaultLatitude);
    }

    getInitialMapZoom(): number | undefined {
        const mapZoom = this.userStoreService.get('zoom');
        return isNumber(mapZoom) ? mapZoom : undefined;
    }

    getZoom(mapboxMap: MapboxMap): number {
        return mapboxMap.getZoom();
    }

    isPopupShowing(): boolean {
        return this.popup.isOpen();
    }

    isPermanentPopupShowing(): boolean {
        return this.permanentPopup.isOpen();
    }

    hidePopup(): void {
        this.popup.remove();
    }

    hidePermanentPopup(): void {
        this.permanentPopup.remove();
    }

    setCursorNormal(mapboxMap: MapboxMap): void {
        mapboxMap.getCanvas().style.cursor = '';
    }

    setCursorPointer(mapboxMap: MapboxMap): void {
        mapboxMap.getCanvas().style.cursor = 'pointer';
    }

    setCursorAllScroll(mapboxMap: MapboxMap): void {
        mapboxMap.getCanvas().style.cursor = 'all-scroll';
    }

    // mapboxgl type for filter is any[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFilter(mapboxMap: MapboxMap, layerId: string, filter: any[]): void {
        if (!mapboxMap.getLayer(layerId)) {
            this.logService.error(
                this.className,
                `Tried to set filter on non-existent layer: ${layerId}`
            );
            return;
        }

        this.logService.debug(this.className, {
            debugMessage: 'set filter',
            filter,
            layerId,
        });
        mapboxMap.setFilter(layerId, filter);
    }

    setMultipleSelectedFilter(
        mapboxMap: MapboxMap,
        layerId: string,
        selectedIds: string[]
    ): void {
        this.setSelectedFilter(mapboxMap, layerId, selectedIds.join(','));
    }

    setSelectedFilter(
        mapboxMap: MapboxMap,
        layerId: string,
        selectedId?: string | number,
        previousId?: string | number
    ): void {
        // possible filters
        // [ 'all', ['==', 'id', 'SELECTED_ID' ] ]
        // [ 'all', ['match', ['get', 'id'], ['SELECTED_ID'], true, false ] ]
        // [ 'all', ['all', ['==', 'id', 'SELECTED_ID' ] ] ]
        selectedId = selectedId || 'SELECTED_ID';
        previousId =
            previousId || this.selectedIdsCache[layerId] || 'SELECTED_ID';
        let didFindResult = false;
        let filter = mapboxMap.getFilter(layerId);

        if (!Array.isArray(filter)) {
            filter = ['all', ['==', 'id', selectedId]];
        } else {
            didFindResult = this.setSubFilterValue(
                filter,
                [previousId],
                selectedId
            );

            if (didFindResult) {
                this.selectedIdsCache[layerId] = selectedId;
            }
            // try again with default selected ID if we failed to find it
            else if (previousId !== 'SELECTED_ID') {
                this.setSelectedFilter(
                    mapboxMap,
                    layerId,
                    selectedId,
                    'SELECTED_ID'
                );
                return;
            }
        }

        this.setFilter(mapboxMap, layerId, filter);
    }

    applyLayerFilters(
        mapboxMap: MapboxMap,
        layerIds: string[],
        selectedIds?: string | number | (string | number)[]
    ): void {
        let isArray = false;
        let selectedId: string | number | undefined;
        if (Array.isArray(selectedIds)) {
            if (selectedIds.length > 1) {
                isArray = true;
            } else {
                selectedId = selectedIds[0];
            }
        } else {
            selectedId = selectedIds;
        }

        layerIds.forEach((layerId) => {
            if (isArray) {
                this.setMultipleSelectedFilter(
                    mapboxMap,
                    layerId,
                    selectedIds as string[]
                );
            } else {
                this.setSelectedFilter(mapboxMap, layerId, selectedId);
            }
        });
    }

    setVehicleTypeFilter(
        mapboxMap: MapboxMap,
        layerId: string,
        vehicleType: VehicleType
    ): void {
        if (!mapboxMap.getLayer(layerId)) {
            this.logService.debug(this.className, {
                debugMessage: 'tried to get non-existent layer',
                layerId,
            });
            return;
        }

        let filter = mapboxMap.getFilter(layerId);
        const filterString = vehicleType === 'SNO' ? 'isSnow' : 'isOrv';

        if (!Array.isArray(filter)) {
            filter = ['all', ['==', filterString, true]];
        } else {
            const didFindResult = this.setSubFilterValue(
                filter,
                ['isSnow', 'isOrv'],
                filterString
            );
            if (!didFindResult && filter[0] === 'all') {
                filter.push(['==', filterString, true]);
            }
        }

        this.setFilter(mapboxMap, layerId, filter);
    }

    setSourceProperty(
        mapboxMap: MapboxMap,
        sourceId: string,
        property: string,
        value: string[]
    ): void {
        this.doWhenSourceLoaded(mapboxMap, sourceId, (source) => {
            this.logService.debug(
                this.className,
                `set map ${property} to ${JSON.stringify(value)}`
            );
            source[property] = value;
        });
    }

    setLayoutProperty(
        mapboxMap: MapboxMap,
        layerId: string,
        property: keyof LayoutSpecification,
        value: string
    ): void {
        this.logService.debug(
            this.className,
            `set ${property} to ${value} on ${layerId}`
        );
        mapboxMap.setLayoutProperty(layerId, property, value);
    }

    setPaintProperty(
        mapboxMap: MapboxMap,
        layerId: string,
        property: keyof PaintSpecification,
        value: number
    ): void {
        this.logService.debug(
            this.className,
            `set ${property} to ${value} on ${layerId}`
        );
        mapboxMap.setPaintProperty(layerId, property, value);
    }

    setSourceData(
        mapboxMap: MapboxMap,
        sourceId: string,
        data: GeoJSON.Feature | GeoJSON.FeatureCollection
    ): void {
        this.doWhenSourceLoaded(mapboxMap, sourceId, (source) => {
            this.logService.debug(this.className, `set source for ${sourceId}`);
            source.setData(data);
        });
    }

    /**
     * @description Shows both permanent and temporary map ride condition popup on map.
     * @param mapboxMap - Mapbox map object.
     * @param rideCondition - Ride condition object.
     * @param isPermanent - True if should show permanent popup; defaults to false.
     */
    showRideConditionPopup(
        mapboxMap: MapboxMap,
        rideCondition: RideCondition,
        isPermanent = false
    ): void {
        if (
            !rideCondition.geoCoordinate ||
            !rideCondition.geoCoordinate.lat ||
            !rideCondition.geoCoordinate.lng
        ) {
            // Don't show popup if we're given a bad lat/lng
            return;
        }

        const componentRef = this.showPopup<RideConditionPopupComponent>(
            mapboxMap,
            rideCondition.geoCoordinate,
            RideConditionPopupComponent,
            isPermanent
        );

        componentRef.instance.rideCondition = rideCondition;
        componentRef.instance.shouldShowReportAbuseLink = isPermanent;
    }

    showPermanentPopup(
        mapboxMap: MapboxMap,
        mapPoint:
            | Adventure
            | Dealer
            | Geofence
            | Place
            | PlannedRide
            | RideArea
            | RideCondition
            | TrackedRide
            | Waypoint
            | PlannedRideWaypoint
    ): Observable<MapPoint> {
        let offset = true;
        if (
            mapPoint instanceof PlannedRide ||
            (mapPoint instanceof PlannedRideWaypoint &&
                mapPoint.originalType !== 'location')
        ) {
            offset = false;
        }
        const componentRef = this.showPopup<MapPointPopupComponent>(
            mapboxMap,
            mapPoint.geoCoordinate as LngLatLike,
            MapPointPopupComponent,
            true,
            offset
        );
        componentRef.instance.mapPoint = mapPoint;

        if (
            !mapPoint.geoCoordinate ||
            !mapPoint.geoCoordinate.lat ||
            !mapPoint.geoCoordinate.lng
        ) {
            // Don't show popup if we're given a bad lat/lng
            return throwError(() => 'bad coordinates');
        }

        return componentRef.instance.addMapPointToPlannedRide.asObservable();
    }

    showTemporaryPopup(
        mapboxMap: MapboxMap,
        mapPoint:
            | Adventure
            | Place
            | PremiumPlace
            | Geofence
            | PlannedRide
            | TrackedRide
            | RideArea
            | RideCondition
            | Waypoint
            | PlannedRideWaypoint
            | PlannedRideViapoint
            | Dealer
            | VehicleLocation
    ): {
        addPoint: Observable<MapPoint>;
        removePoint: Observable<{
            mapPoint: MapPoint;
            deleteSegments: boolean;
        }>;
    } {
        let shouldOffSet = true;
        if (
            mapPoint instanceof PlannedRide ||
            mapPoint instanceof Geofence ||
            mapPoint instanceof PlannedRideWaypoint ||
            mapPoint instanceof PlannedRideViapoint
        ) {
            //revisit after RC-16426 (labels beside Pin; causes pin too high when item not in list)
            shouldOffSet = false;
        }
        const componentRef = this.showPopup<MapPointPopupComponent>(
            mapboxMap,
            mapPoint.geoCoordinate as LngLatLike,
            MapPointPopupComponent,
            false,
            shouldOffSet
        );

        componentRef.instance.mapPoint = mapPoint;

        return {
            addPoint:
                componentRef.instance.addMapPointToPlannedRide.asObservable(),
            removePoint:
                componentRef.instance.removeMapPointFromPlannedRide.asObservable(),
        };
    }

    private doWhenSourceLoaded(
        mapboxMap: MapboxMap,
        sourceId: string,
        callback: (source: GeoJSONSource) => void
    ): void {
        const source = mapboxMap.getSource(sourceId) as GeoJSONSource;

        if (!source) {
            this.logService.debug(
                this.className,
                `waiting for ${sourceId} source to load`
            );

            if (this.sourceLoadedQueue[sourceId] === undefined) {
                this.sourceLoadedQueue[sourceId] = [];
            }
            this.sourceLoadedQueue[sourceId].push(callback);

            if (this.sourceLoadedObserver[sourceId] === undefined) {
                this.sourceLoadAttempts[sourceId] = 0;
                this.sourceLoadedObserver[sourceId] = this.window.setInterval(
                    () => {
                        this.processSourceLoadedQueue(mapboxMap, sourceId);
                    },
                    60
                );
            }
        } else {
            callback(source);
        }
    }

    private processSourceLoadedQueue(
        mapboxMap: MapboxMap,
        sourceId: string
    ): void {
        let source: GeoJSONSource;

        try {
            source = mapboxMap.getSource(sourceId) as GeoJSONSource;
        } catch (err) {
            this.logService.debug(this.className, {
                debugMessage:
                    'error getting source from map while processing source loaded queue',
                originalError: err,
                sourceId,
            });
            this.window.clearInterval(this.sourceLoadedObserver[sourceId]);
            return;
        }

        if (source) {
            this.logService.debug(this.className, `${sourceId} source loaded`);

            for (const queuedItem of this.sourceLoadedQueue[sourceId]) {
                queuedItem(source);
            }

            this.sourceLoadedQueue[sourceId] = [];
            this.window.clearInterval(this.sourceLoadedObserver[sourceId]);
            this.sourceLoadedObserver[sourceId] = undefined;
        } else {
            this.sourceLoadAttempts[sourceId] += 1;

            if (
                this.sourceLoadAttempts[sourceId] >= this.maxSourceLoadAttempts
            ) {
                this.logService.debug(
                    this.className,
                    `timed out waiting for ${sourceId} source to load`
                );
                this.window.clearInterval(this.sourceLoadedObserver[sourceId]);
            }
        }
    }

    private hasLayer(mapboxMap: MapboxMap, layerName: string): boolean {
        return !isEmpty(mapboxMap.getLayer(layerName));
    }

    private copyMapLayer(
        mapboxMap: MapboxMap,
        layerId: string,
        sourceId: string,
        overrides: Partial<LayerSpecification> = {},
        visible = true
    ): LayerSpecification {
        const oldStyleLayer = this.getLayerStyle(
            mapboxMap,
            layerId
        ) as LayerSpecification;

        delete oldStyleLayer['source-layer'];
        const newStyleLayer = merge({}, oldStyleLayer, overrides);
        newStyleLayer.source = sourceId;
        if (!newStyleLayer.layout) {
            newStyleLayer.layout = {};
        }

        // some LayerSpecifications don't have layout.visibility. previously we were always setting this, continue to do so
        (
            newStyleLayer.layout as { visibility: 'visible' | 'none' }
        ).visibility = visible ? 'visible' : 'none';

        return newStyleLayer;
    }

    private isCoordinateWithinPanningDistance(
        mapboxMap: MapboxMap,
        coordinate: GeoCoordinate
    ): boolean {
        return this.getBounds(mapboxMap)
            .resizedBy(this.maxBoundsPanFactor * 2)
            .containsGeoCoordinate(coordinate);
    }

    private isBoundsWithinPanningDistance(
        mapboxMap: MapboxMap,
        targetBounds: GeoBounds
    ): boolean {
        return this.getBounds(mapboxMap)
            .resizedBy(this.maxBoundsPanFactor * 2)
            .containsGeoBounds(targetBounds);
    }

    private setSubFilterValue(
        // mapboxgl type for filter is any[]
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filters: any[],
        searchStrings: (string | number)[],
        value: string | number
    ): boolean {
        const subFilters = [filters];
        while (subFilters.length > 0) {
            const subFilter = subFilters.pop() || [];

            for (let i = subFilter.length - 1; i >= 0; i--) {
                const filter = subFilter[i];

                if (Array.isArray(filter)) {
                    subFilters.push(filter);
                } else if (searchStrings.includes(filter as string | number)) {
                    subFilter[i] = value;
                    return true;
                }
            }
        }

        return false;
    }

    private showPopup<T>(
        mapboxMap: MapboxMap,
        lngLat: LngLatLike,
        componentType: Type<T> = MapPointPopupComponent as Type<T>,
        isPermanent = false,
        offset = false
    ): ComponentRef<T> {
        const componentRef = this.componentFactoryResolver
            .resolveComponentFactory<T>(componentType)
            .create(this.injector);

        this.appRef.attachView(componentRef.hostView);
        const el: HTMLElement = (
            componentRef.hostView as EmbeddedViewRef<unknown>
        ).rootNodes[0];

        let popup: Popup;
        if (isPermanent) {
            popup = this.permanentPopup;
        } else {
            popup = this.popup;
        }

        popup.setLngLat(lngLat);
        popup.setDOMContent(el);
        popup.addTo(mapboxMap);

        if (offset) {
            popup.addClassName('uses-tall-icon');
        } else {
            popup.removeClassName('uses-tall-icon');
        }

        return componentRef;
    }
}

export interface MapboxFeature<
    T extends GeometryObject,
    P extends GeoJSON.GeoJsonProperties = GeoJSON.GeoJsonProperties,
> extends GeoJSON.Feature<T> {
    layer: Layer;
    properties: P;
}
